import { useState } from 'react';

import { isEmpty } from 'lodash';
import { TradingBlockDocumentNameDto } from 'src/dtos';
import { useAccountSelector, useInvestigationListSelector } from 'src/hooks';
import { MSpinner, MTypography } from 'src/lib';
import { AccountHolder, InvestigationCategoryName, TradingBlockInvestigationCipCategory } from 'src/models';
import { Color, Spacing } from 'src/styles';

import { ApplicationCipFailureForm } from './ApplicationCipFailureForm';
import { InvestigationCipFailureForm } from './InvestigationCipFailureForm';

export const AccountInvestigations = () => {
  const [primaryNameOrDateOfBirthSelectedDocumentType, setPrimaryNameOrDateOfBirthSelectedDocumentType] =
    useState<TradingBlockDocumentNameDto | null>(null);
  const [secondaryNameOrDateOfBirthSelectedDocumentType, setSecondaryNameOrDateOfBirthSelectedDocumentType] =
    useState<TradingBlockDocumentNameDto | null>(null);

  const {
    status,
    primaryAccountHolderApplications,
    primaryAccountHolderInvestigations,
    secondaryAccountHolderApplications,
    secondaryAccountHolderInvestigations,
    isInvestigationListLoading,
    isAnyPrimaryAccountHolderDocumentRequested,
    isAnySecondaryAccountHolderDocumentRequested,
  } = useInvestigationListSelector();

  const { account } = useAccountSelector();

  const getAccountHolderName = (accountHolder?: AccountHolder) => {
    return `${accountHolder?.firstName} ${accountHolder?.lastName}`;
  };

  const removeAddressCategoryIfNecessary = ({
    category,
    document,
  }: {
    category: TradingBlockInvestigationCipCategory;
    document: TradingBlockDocumentNameDto | null;
  }): boolean => {
    return !(
      document &&
      [TradingBlockDocumentNameDto.DriversLicense, TradingBlockDocumentNameDto.Passport].includes(document) &&
      category.name === InvestigationCategoryName.Address
    );
  };

  const removePrimaryAddressCategoryIfNecessary = (category: TradingBlockInvestigationCipCategory): boolean =>
    removeAddressCategoryIfNecessary({ category, document: primaryNameOrDateOfBirthSelectedDocumentType });

  const removeSecondaryAddressCategoryIfNecessary = (category: TradingBlockInvestigationCipCategory): boolean =>
    removeAddressCategoryIfNecessary({ category, document: secondaryNameOrDateOfBirthSelectedDocumentType });

  if (isInvestigationListLoading || !status) {
    return <MSpinner />;
  }

  return (
    <>
      {(status.isPending || status.isRejected) && (
        <>
          {account?.type.isJoint && isAnyPrimaryAccountHolderDocumentRequested && (
            <MTypography variant='h4' weight='normal' color={Color.Brand6} className={Spacing.my24}>
              Primary Account Holder ({getAccountHolderName(account?.primaryAccountHolder)})
            </MTypography>
          )}
          {isAnyPrimaryAccountHolderDocumentRequested && (
            <MTypography variant='h5' weight='bold' color={Color.Gray10} className={Spacing.mt24}>
              Additional Information
            </MTypography>
          )}
          {primaryAccountHolderApplications.map(application =>
            application.appCategories
              .filter(category => !isEmpty(category.requestedDocuments))
              .map(appCategory => {
                return (
                  <ApplicationCipFailureForm
                    key={`${appCategory.name}_key`}
                    category={appCategory}
                    uploadedDocuments={application.uploadedDocuments ?? []}
                    accountHolderId={application.accountHolderId}
                    accountId={account?.accountId}
                  />
                );
              }),
          )}
          {primaryAccountHolderInvestigations.map(investigation =>
            investigation.cipCategories
              .filter(category => !isEmpty(category.requestedDocuments))
              .filter(removePrimaryAddressCategoryIfNecessary)
              .map(cipCategory => {
                return (
                  <div key={`${cipCategory?.name}_key`}>
                    <InvestigationCipFailureForm
                      investigationId={investigation.investigationId}
                      category={cipCategory}
                      uploadedDocuments={investigation.uploadedDocuments ?? []}
                      accountId={account?.accountId}
                      onChange={
                        cipCategory.name === InvestigationCategoryName.NameOrDateOfBirth
                          ? documentName => {
                              setPrimaryNameOrDateOfBirthSelectedDocumentType(documentName);
                            }
                          : undefined
                      }
                    />
                  </div>
                );
              }),
          )}

          {account?.type.isJoint && isAnySecondaryAccountHolderDocumentRequested && (
            <>
              <MTypography variant='h4' weight='normal' color={Color.Brand6} className={Spacing.my24}>
                Joint Account Holder ({getAccountHolderName(account?.secondaryAccountHolder)})
              </MTypography>
              <MTypography variant='h5' weight='bold' color={Color.Gray10} className={Spacing.mt24}>
                Additional Information
              </MTypography>
            </>
          )}
          {secondaryAccountHolderApplications.map(application =>
            application.appCategories
              .filter(category => !isEmpty(category.requestedDocuments))
              .map(appCategory => {
                return (
                  <ApplicationCipFailureForm
                    key={`${appCategory.name}_key`}
                    category={appCategory}
                    uploadedDocuments={application.uploadedDocuments ?? []}
                    accountHolderId={application.accountHolderId}
                    accountId={account?.accountId}
                  />
                );
              }),
          )}
          {secondaryAccountHolderInvestigations.map(investigation =>
            investigation.cipCategories
              .filter(category => !isEmpty(category.requestedDocuments))
              .filter(removeSecondaryAddressCategoryIfNecessary)
              .map(cipCategory => {
                return (
                  <div key={`${cipCategory?.name}_key`}>
                    <InvestigationCipFailureForm
                      investigationId={investigation.investigationId}
                      category={cipCategory}
                      uploadedDocuments={investigation.uploadedDocuments ?? []}
                      accountId={account?.accountId}
                      onChange={
                        cipCategory.name === InvestigationCategoryName.NameOrDateOfBirth
                          ? documentName => {
                              setSecondaryNameOrDateOfBirthSelectedDocumentType(documentName);
                            }
                          : undefined
                      }
                    />
                  </div>
                );
              }),
          )}
        </>
      )}
    </>
  );
};
